import { Alert, Button, Loader, Modal, Stack, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { listLibrary, listSheets } from '../api/gen';
import { useOrg } from '../lib/session';

/** One sheet a child can say is in front of them. */
export interface PickedSheet {
  id: string;
  title: string;
}

interface Group {
  label: string;
  sheets: PickedSheet[];
}

/**
 * "Which sheet do you have in front of you?" — the child's way into a
 * session over a printed Arbeitsblatt. The family's own sheets come first,
 * newest first, because they are the ones a parent just printed.
 */
export function SheetPicker({
  opened,
  onClose,
  onPick,
  color,
}: {
  opened: boolean;
  onClose: () => void;
  onPick: (sheet: PickedSheet) => void;
  color: string;
}) {
  const org = useOrg();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['child-sheets'],
    enabled: opened,
    queryFn: async (): Promise<Group[]> => {
      const [written, library] = await Promise.all([
        unwrap(listSheets({ headers: org, query: { limit: 30, sort: 'createdAt', sortDir: 'desc' } })),
        unwrap(listLibrary({ headers: org })),
      ]);
      const groups: Group[] = [];
      if (written && written.data.length > 0) {
        groups.push({
          label: 'Für dich geschrieben',
          sheets: written.data.map((s) => ({ id: s.id, title: `${s.title} (${s.subject})` })),
        });
      }
      for (const set of library?.sets ?? []) {
        groups.push({
          label: `${set.subject} · Klasse ${set.grade} · ${set.topic}`,
          sheets: set.sheets.map((s, i) => ({ id: s.id, title: `Blatt ${i + 1}: ${s.title}` })),
        });
      }
      return groups;
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Welches Blatt hast du vor dir?" size="lg">
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <Stack>
          <Alert color="red">Die Blätter lassen sich gerade nicht laden.</Alert>
          <Button variant="light" onClick={() => void refetch()}>
            Nochmal
          </Button>
        </Stack>
      ) : (
        <Stack gap="lg">
          {(data ?? []).map((group) => (
            <Stack key={group.label} gap="xs">
              <Text size="sm" c="dimmed" fw={600}>
                {group.label}
              </Text>
              {group.sheets.map((sheet) => (
                <UnstyledButton
                  key={sheet.id}
                  onClick={() => onPick(sheet)}
                  p="md"
                  style={{
                    border: `2px solid var(--mantine-color-${color}-3)`,
                    borderRadius: 'var(--mantine-radius-lg)',
                  }}
                >
                  <Text fw={600} size="lg">
                    {sheet.title}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          ))}
        </Stack>
      )}
    </Modal>
  );
}
